import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2, ArrowLeft, FileText, Link2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogContentWithoutClose,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { MultiSelect, type Option } from '../../../components/ui/multi-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { CollapsibleFilterBar } from '../../project/components/dashboard/materials/tabs/Filters/CollapsibleFilterBar';
import { pipefitterGenerateReportPipeData, pipefitterGenerateReportPipeDataPipewise } from '../../../redux/Pipefitter/actions';
import {
  mapPipeDataListRowsToPickerItems,
  type PipeDataListPickerItem,
} from '../api/pipeDataList';
import { isPipeFlagOn } from '../helpers/mapPipeStatusTableRows';
import { fetchPipeDataPreview, normalizePipeDataPreview } from '../api/pipeDataPreview';
import type { ReportHistoryReportData } from '../api/reportHistory';
import { PipeDataReportVisualization } from './PipeDataReportVisualization';
import { PipeCompatibilityReportModal } from './PipeCompatibilityReportModal';
import { SendReportEmailModal } from './SendReportEmailModal';
import type { DashboardProject } from '../../project/types/project';
import type { PipeStatusItem } from '../types';
import { PIPEFITTER_FULLSCREEN_DIALOG_CLASS, PIPEFITTER_STACKED_DIALOG_OVERLAY_CLASS } from '../constants/uiConstants';
import {
  buildPipeDataLargeSelectionDescription,
  isLargePipeDataSelection,
} from '../helpers/reportGenerateThresholds';

interface PipeDataReportGenerateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: DashboardProject;
  selectedPipe?: {
    pipeId: string;
    displayName?: string;
    pipeName?: string;
    groupId?: string | null;
  } | null;
  initialSelectedIds?: string[];
  /** Called after the server accepts an async report generation request. */
  onReportGenerated?: (pipeStatusIds?: string[]) => void;
  /** Full-screen shell when opened above another pipefitter dialog (e.g. report history detail). */
  dialogClassName?: string;
}

export const PipeDataReportGenerateModal: React.FC<PipeDataReportGenerateModalProps> = ({
  open,
  onOpenChange,
  project,
  selectedPipe,
  initialSelectedIds = [],
  onReportGenerated,
  dialogClassName,
}) => {
  const dispatch = useDispatch();
  const authUserEmail = useSelector(
    (state: { auth?: { user?: { email?: string } } }) => state.auth?.user?.email?.trim() ?? ''
  );
  const projectNumber = project?.project?.projectNumber ?? '';
  const projectId = project?.project?.projectId;
  const projectName = project?.project?.projectName ?? '';

  // Outside PipeFilter already loads getPipeStatusList into Redux — reuse that list.
  // Do not call getPipeDataList again (it can return a different/shorter count).
  const reduxPipeStatusDataRaw = useSelector((state: any) => state.pipefitterReports?.pipeStatusData);
  const reduxPipeStatusData = Array.isArray(reduxPipeStatusDataRaw)
    ? reduxPipeStatusDataRaw
    : reduxPipeStatusDataRaw
      ? [reduxPipeStatusDataRaw]
      : [];
  const isReportsLoading = useSelector(
    (state: any) => state.pipefitterReports?.isReportsLoading || false
  );
  const pipeStatusLoadingMore = useSelector(
    (state: any) => state.pipefitterReports?.pipeStatusLoadingMore || false
  );

  /** Insights-style search + Pipe ID dropdown — empty filter = show all. */
  const [pipeSearch, setPipeSearch] = useState('');
  const [pipeIdFilter, setPipeIdFilter] = useState<string[]>([]);
  const [selectedPipeIds, setSelectedPipeIds] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingPipewise, setIsGeneratingPipewise] = useState(false);
  /** Which generate flow opened the email confirm modal. */
  const generateLayoutRef = useRef<'endwise' | 'pipewise'>('endwise');
  const [showGenerateEmailModal, setShowGenerateEmailModal] = useState(false);
  const [previewData, setPreviewData] = useState<ReportHistoryReportData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [compatibilityModalOpen, setCompatibilityModalOpen] = useState(false);
  const previewRequestRef = useRef(0);
  const wasOpenRef = useRef(false);

  const isPipeListLoading = isReportsLoading || pipeStatusLoadingMore;

  const pipeList: PipeDataListPickerItem[] = useMemo(() => {
    // Pipe Data report: exclude archived pipes only (flange still allowed).
    const activeRows = (reduxPipeStatusData as PipeStatusItem[]).filter(
      (row) => !isPipeFlagOn(row.isArchive)
    );
    return mapPipeDataListRowsToPickerItems(activeRows);
  }, [reduxPipeStatusData]);

  const initialSelectedIdsKey = useMemo(
    () =>
      [...initialSelectedIds]
        .map(String)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .join(','),
    [initialSelectedIds]
  );

  const buildSelectionFromInitialIds = useCallback(
    (rows: PipeDataListPickerItem[] = pipeList) => {
      const rawIds = initialSelectedIds.map(String).filter(Boolean);
      if (rawIds.length === 0) return new Set<string>();

      const byStatusId = new Map(rows.map((p) => [p.id, p.id]));
      const byPipeId = new Map(rows.map((p) => [p.pipeID, p.id]));
      const next = new Set<string>();
      for (const raw of rawIds) {
        if (byStatusId.has(raw)) {
          next.add(raw);
          continue;
        }
        const matched = byPipeId.get(raw);
        // Only include IDs present in the active (non-archived) picker list.
        if (matched) next.add(matched);
      }
      return next;
    },
    [initialSelectedIds, pipeList]
  );

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    const justClosed = !open && wasOpenRef.current;
    wasOpenRef.current = open;

    if (justClosed) {
      setSelectedPipeIds(new Set());
      setIsGenerating(false);
      setIsGeneratingPipewise(false);
      setShowGenerateEmailModal(false);
      setPreviewData(null);
      setPreviewError(null);
      setPreviewLoading(false);
      setCompatibilityModalOpen(false);
      previewRequestRef.current += 1;
      return;
    }

    if (!open) return;

    if (justOpened) {
      setPipeSearch('');
      setPipeIdFilter([]);
      setIsGenerating(false);
      setIsGeneratingPipewise(false);
      setShowGenerateEmailModal(false);
      setSelectedPipeIds(buildSelectionFromInitialIds());
    }
  }, [open, buildSelectionFromInitialIds]);

  useEffect(() => {
    if (!open || isPipeListLoading || pipeList.length === 0 || !initialSelectedIdsKey) return;
    setSelectedPipeIds((prev) => {
      const reconciled = buildSelectionFromInitialIds(pipeList);
      if (reconciled.size === 0) return prev;
      const prevMatchesLoaded = Array.from(prev).some((id) => pipeList.some((p) => p.id === id));
      if (prev.size === 0 || !prevMatchesLoaded) return reconciled;
      return prev;
    });
  }, [open, isPipeListLoading, pipeList, initialSelectedIdsKey, buildSelectionFromInitialIds]);

  const pipeIdOptions: Option[] = useMemo(() => {
    const ids = Array.from(new Set(pipeList.map((p) => p.pipeID).filter(Boolean)));
    ids.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return ids.map((id) => ({ value: id, label: id }));
  }, [pipeList]);

  const visiblePipes = useMemo(() => {
    const q = pipeSearch.trim().toLowerCase();
    const selected = pipeIdFilter.length > 0 ? new Set(pipeIdFilter) : null;
    return pipeList.filter((p) => {
      const matchesSearch = !q || p.pipeID.toLowerCase().includes(q);
      const matchesDropdown = !selected || selected.has(p.pipeID);
      return matchesSearch && matchesDropdown;
    });
  }, [pipeList, pipeSearch, pipeIdFilter]);

  const hasPipeListFilters = Boolean(pipeSearch.trim()) || pipeIdFilter.length > 0;

  const selectedCount = selectedPipeIds.size;
  const hasVisiblePipes = visiblePipes.length > 0;
  const isLargeSelection = isLargePipeDataSelection(selectedCount);
  const canUseLoginEmail = Boolean(authUserEmail);

  const isAllVisibleSelected =
    visiblePipes.length > 0 && visiblePipes.every((p) => selectedPipeIds.has(p.id));
  const isSomeVisibleSelected =
    visiblePipes.some((p) => selectedPipeIds.has(p.id)) && !isAllVisibleSelected;

  const pipeIdsForApiKey = useMemo(() => {
    if (selectedCount === 0) return '';
    return Array.from(selectedPipeIds)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .join(',');
  }, [selectedCount, selectedPipeIds]);

  const loadPreview = useCallback(async (pipeIdsKey: string) => {
    const pipeId = selectedPipe?.pipeId;
    const selectedPipeStatusIds = pipeIdsKey ? pipeIdsKey.split(',').filter(Boolean) : [];
    if (!projectNumber || !pipeId || selectedPipeStatusIds.length === 0) {
      setPreviewData(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    const requestId = ++previewRequestRef.current;
    try {
      const raw = await fetchPipeDataPreview({
        projectNumber,
        pipeId,
        projectId,
        selectedPipeStatusIds,
        ...(selectedPipe?.groupId != null
          ? { groupId: String(selectedPipe.groupId).trim() }
          : {}),
      });
      if (requestId !== previewRequestRef.current) return;
      setPreviewData(normalizePipeDataPreview(raw));
    } catch (e: unknown) {
      if (requestId !== previewRequestRef.current) return;
      const message = e instanceof Error ? e.message : 'Failed to load pipe data preview';
      setPreviewError(message);
      setPreviewData(null);
    } finally {
      if (requestId === previewRequestRef.current) {
        setPreviewLoading(false);
      }
    }
  }, [projectNumber, projectId, selectedPipe?.pipeId, selectedPipe?.groupId]);

  const loadPreviewRef = useRef(loadPreview);
  loadPreviewRef.current = loadPreview;

  useEffect(() => {
    if (!open) return;

    if (!pipeIdsForApiKey) {
      setPreviewData(null);
      setPreviewError(null);
      setPreviewLoading(false);
      previewRequestRef.current += 1;
      return;
    }

    const timer = window.setTimeout(() => {
      void loadPreviewRef.current(pipeIdsForApiKey);
    }, 300);

    return () => {
      window.clearTimeout(timer);
      previewRequestRef.current += 1;
    };
  }, [open, pipeIdsForApiKey]);

  const pipeIdsForApi = useMemo(
    () => (pipeIdsForApiKey ? pipeIdsForApiKey.split(',').filter(Boolean) : []),
    [pipeIdsForApiKey]
  );

  const handleTogglePipe = (id: string, checked: boolean) => {
    setSelectedPipeIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSelectAllVisible = (checked: boolean) => {
    if (checked) {
      setSelectedPipeIds((prev) => {
        const next = new Set(prev);
        visiblePipes.forEach((p) => next.add(p.id));
        return next;
      });
    } else {
      setSelectedPipeIds((prev) => {
        const next = new Set(prev);
        visiblePipes.forEach((p) => next.delete(p.id));
        return next;
      });
    }
  };

  const generateEmailDescription = isLargeSelection && authUserEmail
    ? buildPipeDataLargeSelectionDescription(selectedCount, authUserEmail)
    : 'Enter your email address to receive the PDF once the report has been generated. Separate multiple addresses with commas.';

  const anyGenerating = isGenerating || isGeneratingPipewise;

  const handleConfirmGenerateReport = (emails: string[], layout: 'endwise' | 'pipewise' = 'endwise') => {
    if (!projectNumber || anyGenerating || pipeIdsForApi.length === 0) return;
    if (isLargeSelection && emails.length === 0) return;

    const isPipewise = layout === 'pipewise';
    if (isPipewise) setIsGeneratingPipewise(true);
    else setIsGenerating(true);

    const action = isPipewise
      ? pipefitterGenerateReportPipeDataPipewise
      : pipefitterGenerateReportPipeData;

    dispatch(
      action(
        {
          projectNumber,
          projectId,
          pipeIds: pipeIdsForApi,
          pipeId: selectedPipe?.pipeId,
          reportName: isPipewise ? 'Pipe Data Report (Pipewise)' : 'Pipe Data Report',
          ...(isPipewise ? { tableLayout: 'pipewise' as const } : {}),
          ...(selectedPipe?.groupId != null
            ? { groupId: String(selectedPipe.groupId).trim() }
            : {}),
          ...(emails[0] ? { notifyEmail: emails[0] } : {}),
        },
        () => {
          setIsGenerating(false);
          setIsGeneratingPipewise(false);
          setShowGenerateEmailModal(false);
          onReportGenerated?.(pipeIdsForApi);
          onOpenChange(false);
        },
        () => {
          setIsGenerating(false);
          setIsGeneratingPipewise(false);
        }
      )
    );
  };

  const handleOpenGenerateEmailModal = (layout: 'endwise' | 'pipewise' = 'endwise') => {
    if (!projectNumber || anyGenerating || pipeIdsForApi.length === 0) return;

    generateLayoutRef.current = layout;

    if (!isLargeSelection) {
      handleConfirmGenerateReport(authUserEmail ? [authUserEmail] : [], layout);
      return;
    }

    if (!canUseLoginEmail) return;
    setShowGenerateEmailModal(true);
  };

  const pipeSizeLabel =
    selectedPipe?.displayName ?? selectedPipe?.pipeName ?? selectedPipe?.pipeId ?? '';

  const headerInfoItems = useMemo(() => {
    const items: { label: string; value: string; bgColor: string }[] = [];
    if (projectName) {
      items.push({ label: 'Project Name', value: projectName, bgColor: 'bg-indigo-100 text-indigo-800' });
    }
    if (projectNumber) {
      items.push({ label: 'Project Number', value: projectNumber, bgColor: 'bg-cyan-100 text-cyan-800' });
    }
    if (pipeSizeLabel) {
      items.push({
        label: 'Pipe size',
        value: pipeSizeLabel,
        bgColor: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
      });
    }
    return items;
  }, [projectName, projectNumber, pipeSizeLabel]);

  const hasPreviewContent = Boolean(
    previewData &&
      ((previewData.pipesList?.length ?? 0) > 0 || (previewData.graphPipes?.length ?? 0) > 0)
  );

  const showPreviewInitialLoader = previewLoading && !previewData;

  const dialogShellClass = dialogClassName ?? PIPEFITTER_FULLSCREEN_DIALOG_CLASS;
  const DialogShell = dialogClassName ? DialogContentWithoutClose : DialogContent;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogShell
        className={dialogShellClass}
        overlayClassName={dialogClassName ? PIPEFITTER_STACKED_DIALOG_OVERLAY_CLASS : undefined}
      >
        <div className="flex h-screen flex-col overflow-hidden">
        <DialogHeader className="shrink-0 border-b border-[#E2E5EA] bg-white px-4 pb-3 pt-4 sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
                variant="outline"
                size="icon"
                disabled={anyGenerating}
                aria-label="Back to reports list"
              >
                <ArrowLeft className="h-4 w-4 text-text-grey" />
              </Button>
              <div className="min-w-0">
                <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                  Pipe Data Report
                </DialogTitle>
                {headerInfoItems.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {headerInfoItems.map((item, idx) => (
                      <span
                        key={`${item.label}-${idx}`}
                        className={`${item.bgColor} px-2 py-1 rounded text-xs font-medium`}
                      >
                        {item.label}: {item.value}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setCompatibilityModalOpen(true)}
                disabled={!projectNumber || !selectedPipe?.pipeId}
              >
                <Link2 className="h-4 w-4 mr-1.5" />
                Pipe Compatibility
              </Button> */}
              {/* <Button
                type="button"
                size="sm"
                onClick={() => handleOpenGenerateEmailModal('endwise')}
                loading={isGenerating}
                loadingText="Starting…"
                disabled={
                  anyGenerating ||
                  selectedCount === 0 ||
                  !projectNumber ||
                  (isLargeSelection && !canUseLoginEmail)
                }
                title={
                  isLargeSelection && !canUseLoginEmail
                    ? 'Sign in with an email address to generate large pipe data reports'
                    : undefined
                }
              >
                <FileText className="h-4 w-4 mr-1.5" />
                Generate Report
              </Button> */}

              <Button
                type="button"
                size="sm"
               // variant="outline"
                onClick={() => handleOpenGenerateEmailModal('pipewise')}
                loading={isGeneratingPipewise}
                loadingText="Starting…"
                disabled={
                  anyGenerating ||
                  selectedCount === 0 ||
                  !projectNumber ||
                  (isLargeSelection && !canUseLoginEmail)
                }
                title={
                  isLargeSelection && !canUseLoginEmail
                    ? 'Sign in with an email address to generate large pipewise reports'
                    : 'One PDF row per pipe ID with End A / End B columns'
                }
              >
                <FileText className="h-4 w-4 mr-1.5" />
                Generate Report
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          <aside className="w-80 sm:w-96 flex-shrink-0 border-r border-gray-200 flex flex-col bg-gray-50/50">
            <div className="px-3 pt-3 pb-1 border-b border-gray-100">
              <CollapsibleFilterBar
                searchQuery={pipeSearch}
                onSearchChange={setPipeSearch}
                searchPlaceholder="Search by pipe ID…"
                activeFilterCount={hasPipeListFilters ? Math.max(pipeIdFilter.length, 1) : 0}
                filteredCount={visiblePipes.length}
                filteredCountLabel="pipes"
                onResetFilters={() => {
                  setPipeSearch('');
                  setPipeIdFilter([]);
                }}
              >
                <MultiSelect
                  options={pipeIdOptions}
                  selected={pipeIdFilter}
                  onChange={setPipeIdFilter}
                  placeholder="Pipe ID"
                  className="w-full"
                  isSearchable
                />
              </CollapsibleFilterBar>
            </div>

            <div className="flex-1 overflow-y-auto px-3 py-2 min-h-0">
              {isPipeListLoading && pipeList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <p className="text-xs">Loading pipes…</p>
                </div>
              ) : !hasVisiblePipes ? (
                <p className="text-xs text-gray-400 py-3 text-center">
                  {pipeList.length === 0 ? 'No pipes loaded.' : 'No pipes match your search or filters.'}
                </p>
              ) : (
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm">
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-10 text-center">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={isAllVisibleSelected}
                            ref={(el) => {
                              if (el) (el as any).indeterminate = isSomeVisibleSelected;
                            }}
                            onCheckedChange={(c) => handleSelectAllVisible(c === true)}
                            className="cursor-pointer h-3.5 w-3.5"
                            aria-label={isAllVisibleSelected ? 'Deselect all visible pipes' : 'Select all visible pipes'}
                          />
                        </div>
                      </TableHead>
                      <TableHead className="w-16 text-center text-xs">SL No</TableHead>
                      <TableHead className="text-xs">Pipe ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visiblePipes.map((pipe, index) => {
                      const isSelected = selectedPipeIds.has(pipe.id);
                      return (
                        <TableRow key={pipe.id} className="hover:bg-blue-50/40">
                          <TableCell className="w-10 text-center">
                            <div className="flex items-center justify-center">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(c) => handleTogglePipe(pipe.id, c === true)}
                                className="cursor-pointer h-3.5 w-3.5"
                                aria-label={`${isSelected ? 'Deselect' : 'Select'} ${pipe.pipeID}`}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-xs text-gray-600 tabular-nums">
                            {index + 1}
                          </TableCell>
                          <TableCell className="text-xs font-medium text-gray-900 whitespace-nowrap">
                            {pipe.pipeID}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>

            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 space-y-2">
              {/* {isLargeSelection ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                  {canUseLoginEmail
                    ? `Large selection (${selectedCount} pipes): generation may take longer. The report will be emailed to ${authUserEmail}.`
                    : 'Large selection: sign in with an email address to generate this report.'}
                </p>
              ) : null} */}
              <p className="text-xs text-gray-500">
                {selectedCount > 0 ? (
                  <>
                    <span className="font-semibold text-blue-600">{selectedCount}</span> pipe
                    {selectedCount !== 1 ? 's' : ''} selected for report
                  </>
                ) : (
                  'Select one or more pipes to preview and generate a report'
                )}
              </p>
            </div>
          </aside>

          <div className="flex-1 flex flex-col overflow-y-auto p-5 sm:p-6 min-h-0">
            {showPreviewInitialLoader ? (
              <div className="flex flex-col items-center justify-center flex-1 py-16 gap-2 text-gray-400">
                <Loader2 className="h-6 w-6 animate-spin" />
                <p className="text-sm">Loading preview…</p>
              </div>
            ) : previewError && !previewData ? (
              <div className="flex flex-col items-center justify-center flex-1 py-16 gap-3 text-center">
                <p className="text-sm text-red-600">{previewError}</p>
                <Button size="sm" variant="outline" onClick={() => void loadPreview(pipeIdsForApiKey)}>
                  Retry
                </Button>
              </div>
            ) : hasPreviewContent && previewData ? (
              <div className="relative flex-1 min-h-0">
                {previewLoading && (
                  <div className="absolute inset-0 z-10 flex items-start justify-end p-2 pointer-events-none">
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-white/90 border border-gray-200 px-2 py-1 text-xs text-gray-500 shadow-sm">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Updating…
                    </span>
                  </div>
                )}
                <PipeDataReportVisualization reportData={previewData} />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center flex-1 py-16 gap-2 text-gray-400">
                <p className="text-sm">
                  {selectedCount === 0
                    ? 'Select pipes from the list to preview report data.'
                    : 'No preview data for the current selection.'}
                </p>
              </div>
            )}
          </div>
        </div>
        </div>
      </DialogShell>
      </Dialog>

      <PipeCompatibilityReportModal
        open={compatibilityModalOpen}
        onOpenChange={setCompatibilityModalOpen}
        project={project}
        selectedPipe={selectedPipe}
      />

      <SendReportEmailModal
        open={showGenerateEmailModal && isLargeSelection}
        onOpenChange={(next) => {
          if (!anyGenerating) setShowGenerateEmailModal(next);
        }}
        projectNumber={projectNumber}
        title="Generate Pipe Data Report"
        description={generateEmailDescription}
        initialEmail={authUserEmail}
        emailReadOnly={isLargeSelection}
        isSending={anyGenerating}
        submitLabel="Generate Report"
        submittingLabel="Starting…"
        onSend={(emails) => handleConfirmGenerateReport(emails, generateLayoutRef.current)}
      />
    </>
  );
};
